import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { isViewerHome } from '../utils/access';
import { useApi } from '../hooks/useApi';
import { 
  FiActivity,
  FiRefreshCw,
  FiAward,
  FiAlertTriangle,
  FiPieChart,
} from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import useDashboardPermissions from '../hooks/useDashboardPermissions';
import HomeDashboardSection from '../components/HomeDashboardSection';
import BandejaMes from '../components/BandejaMes';
import CobranzasInicioWidget from '../components/CobranzasInicioWidget';
import CollectionCycleMonthCard from '../components/CollectionCycleMonthCard';
import WeeklyGuidanceCard from '../components/WeeklyGuidanceCard';
import ContadorNoteBanner from '../components/ContadorNoteBanner';
import { buildBandeja, buildCobrarHoy } from '../utils/bandeja';
import { FormAlert } from '../components/form';
import { canAccessPyl } from '../utils/pylAccess';
import { getUserPlanInfo, getPlanBadgeClasses } from '../utils/planUtils';
import { canAccessPresupuestos } from '../utils/empresarialAccess';
import { canAccessCalendarioFiscal, canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { hasMultimoneda } from '../utils/multimoneda';
import { formatMoney } from '../utils/money';
import { runRateCopy } from '../utils/fiscal';
import CurrencyViewToggle from '../components/CurrencyViewToggle';
import {
  VISTA_CONSOLIDADO,
  EMPTY_FLUJO,
  displayCurrency,
  pickFlujo,
  vistaHint,
  mergeMonedas,
  shouldShowCurrencyToggle,
} from '../utils/currencyView';
import OnboardingChecklist from '../components/OnboardingChecklist';
import MonthCloseChecklist from '../components/MonthCloseChecklist';
import ContadorPuenteCard from '../components/ContadorPuenteCard';
import ContextualAssistantStrip from '../components/ContextualAssistantStrip';
import MonthCompareCard from '../components/MonthCompareCard';
import {
  isOnboardingComplete,
  isActivationComplete,
  getOnboardingProgress,
  ONBOARDING_QUERY,
} from '../utils/onboarding';
import { isReadOnlyUser } from '../utils/access';
import PlatformLedgerPanel from '../components/PlatformLedgerPanel';
import BetaProPageBanner from '../components/BetaProPageBanner';

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Inicio = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();
  const { canAccessDashboard, loading: permissionsLoading } = useDashboardPermissions();
  const hasDashboardMetrics = canAccessDashboard();

  useEffect(() => {
    if (isViewerHome(user)) navigate('/contador', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await secureFetch('/api/platform-ledger/me');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setIsLedgerAccount(Boolean(data.isLedgerAccount));
      } catch {
        if (!cancelled) setIsLedgerAccount(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, secureFetch]);

  const [stats, setStats] = useState({
    clientes: 0,
    facturas: 0,
    egresos: 0,
    ingresos: 0,
    pendientes: 0,
    crecimiento: 0,
    eficiencia: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLedgerAccount, setIsLedgerAccount] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [onboardingCollapsed, setOnboardingCollapsed] = useState(false);
  const [flujoRaw, setFlujoRaw] = useState({
    desde: null,
    hasta: null,
    ...EMPTY_FLUJO,
    monedas: [],
    enArs: EMPTY_FLUJO,
    porMoneda: {},
    sinCotizacion: 0,
  });
  const [currencyVista, setCurrencyVista] = useState(VISTA_CONSOLIDADO);
  const [dashboardMonedas, setDashboardMonedas] = useState([]);
  const [isLoadingFlujoMes, setIsLoadingFlujoMes] = useState(false);
  const [dashboardRefresh, setDashboardRefresh] = useState(0);
  const [fiscalResumen, setFiscalResumen] = useState(null);
  const [bandejaInvoices, setBandejaInvoices] = useState([]);
  const hasPylAccess = canAccessPyl(user);
  const hasPresupuestosAccess = canAccessPresupuestos(user);
  const hasProfesional = canAccessProfesionalFeatures(user);
  const hasCalendarioFiscal = canAccessCalendarioFiscal(user);
  const [lowScoreClients, setLowScoreClients] = useState([]);
  const [atRiskClients, setAtRiskClients] = useState([]);
  const [fiscalChecklist, setFiscalChecklist] = useState(null);
  const planInfo = useMemo(() => getUserPlanInfo(user), [user]);
  const [budgetAlertas, setBudgetAlertas] = useState([]);
  const readOnly = isReadOnlyUser(user);

  // Función para cargar estadísticas reales
  const fetchStats = useCallback(async () => {
    if (!token || isLoadingStats) return;
    
    try {
      setIsLoadingStats(true);
      setLoading(true);
      setError('');
      
      // Obtener ID del usuario para filtrar datos
      const userId = user?.id || user?._id;
      const empresaId = user?.empresaId || user?.empresa?.id;
      
      if (!userId) {
        throw new Error('No se pudo identificar al usuario');
      }
      
      // Construir URLs con filtros de usuario - FORZAR SIEMPRE
      const resumenUrl = `/api/invoices-dashboard/resumen?userId=${userId}&forceUserFilter=true&preventGlobalData=true&userSpecificOnly=true`;
      const impagasUrl = `/api/invoices-dashboard/impagas?userId=${userId}&forceUserFilter=true&preventGlobalData=true&userSpecificOnly=true`;
      const impagasMesUrl = `/api/invoices-dashboard/impagas?periodo=mes&userId=${userId}&forceUserFilter=true&preventGlobalData=true&userSpecificOnly=true`;
      const mesAnteriorUrl = `/api/invoices-dashboard/resumen?periodo=mes&userId=${userId}&forceUserFilter=true&preventGlobalData=true&userSpecificOnly=true`;
      
      // Headers personalizados para forzar filtro de usuario
      const userSpecificHeaders = {
        'X-Force-User-Filter': 'true',
        'X-Prevent-Global-Data': 'true',
        'X-User-Specific-Only': 'true'
      };
      
      // Obtener resumen del dashboard y egresos para onboarding
      const [resumenRes, expensesRes, clientsRes, onboardingRes] = await Promise.all([
        secureFetch(resumenUrl, { headers: userSpecificHeaders }),
        secureFetch('/api/expenses', { headers: userSpecificHeaders }),
        secureFetch('/api/clients', { headers: userSpecificHeaders }),
        secureFetch('/api/onboarding/status', { headers: userSpecificHeaders }),
      ]);
      if (!resumenRes.ok) {
        throw new Error('Error al obtener resumen del dashboard');
      }
      const resumen = await resumenRes.json();
      let egresosCount = 0;
      if (expensesRes.ok) {
        const expenses = await expensesRes.json();
        egresosCount = Array.isArray(expenses) ? expenses.length : 0;
      }
      let clientesCount = resumen.clientesUnicos || 0;
      if (clientsRes.ok) {
        const clients = await clientsRes.json();
        clientesCount = Math.max(clientesCount, Array.isArray(clients) ? clients.length : 0);
      }

      let onboardingData = null;
      if (onboardingRes.ok) {
        onboardingData = await onboardingRes.json();
        if (onboardingData?.counts?.clientes != null) {
          clientesCount = Math.max(clientesCount, onboardingData.counts.clientes);
        }
      }
      // Log para debug
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 INICIO: Datos del resumen recibidos:', {
          usuario: user?.email,
          userId,
          empresaId,
          resumen,
          url: resumenUrl
        });
      }
      
      // Obtener facturas impagas
      const impagasRes = await secureFetch(impagasUrl, { headers: userSpecificHeaders });
      if (!impagasRes.ok) {
        throw new Error('Error al obtener facturas impagas');
      }
      const impagas = await impagasRes.json();

      // Obtener impagas del mes para eficiencia mensual
      let impagasMes = { cantidad: 0, monto: 0 };
      try {
        const impagasMesRes = await secureFetch(impagasMesUrl, { headers: userSpecificHeaders });
        if (impagasMesRes.ok) {
          impagasMes = await impagasMesRes.json();
        }
      } catch (err) {
        impagasMes = { cantidad: 0, monto: 0 };
      }
      
      // Calcular métricas de crecimiento (comparar con mes anterior)
      let crecimiento = 0;
      try {
        // Obtener facturas del mes anterior para comparar
        const mesAnteriorRes = await secureFetch(mesAnteriorUrl, { headers: userSpecificHeaders });
        if (mesAnteriorRes.ok) {
          const mesAnterior = await mesAnteriorRes.json();
          
          // Calcular crecimiento basado en ingresos del mes actual vs mes anterior
          const ingresosMesActual = resumen.facturasMes > 0 ? resumen.totalIngresos : 0;
          const ingresosMesAnterior = mesAnterior.facturasMes > 0 ? mesAnterior.totalIngresos : 0;
          
          if (ingresosMesActual > 0 && ingresosMesAnterior > 0) {
            crecimiento = Math.round(((ingresosMesActual - ingresosMesAnterior) / ingresosMesAnterior) * 100);
          } else if (ingresosMesActual > 0 && ingresosMesAnterior === 0) {
            crecimiento = 100; // Si no había ingresos el mes anterior, es 100% de crecimiento
          } else if (ingresosMesActual === 0 && ingresosMesAnterior > 0) {
            crecimiento = -100; // Si no hay ingresos este mes pero había el anterior, es -100%
          }
        }
      } catch (err) {
        crecimiento = 0;
      }
      
      // Calcular eficiencia DEL MES (pagadas / total del mes)
      const totalMes = resumen.facturasMes || 0;
      const pagadasMes = Math.max(totalMes - (impagasMes.cantidad || 0), 0);
      const eficiencia = totalMes > 0 ? Math.round((pagadasMes / totalMes) * 100) : 100;
      
      const newStats = {
        branding: Boolean(onboardingData?.progress?.branding),
        clientes: clientesCount,
        facturas: resumen.totalFacturas || 0,
        egresos: egresosCount,
        collection: Boolean(onboardingData?.progress?.collection),
        ingresos: Number(resumen.totalIngresos) || 0,
        pendientes: impagas.cantidad || 0,
        crecimiento: Math.max(crecimiento, 0), // No mostrar crecimiento negativo por ahora
        eficiencia: eficiencia
      };

      setStats(newStats);
      setLastUpdated(new Date());
      setError(''); // Limpiar errores previos si la carga fue exitosa
      
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      setError('Error al cargar estadísticas. Intenta de nuevo.');
      
      // Mantener estadísticas anteriores en caso de error
    } finally {
      setLoading(false);
      setIsLoadingStats(false);
    }
  }, [token, secureFetch, isLoadingStats, user]);

  const fetchFlujoMes = useCallback(async () => {
    if (!token || isLoadingFlujoMes) return;
    try {
      setIsLoadingFlujoMes(true);
      const now = new Date();
      const desde = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
      const hasta = formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      const res = await secureFetch(`/api/reportes/flujo-caja?desde=${desde}&hasta=${hasta}`);
      if (!res.ok) throw new Error('Error al cargar flujo de caja');
      const data = await res.json();
      setFlujoRaw({
        desde: data.desde,
        hasta: data.hasta,
        ...EMPTY_FLUJO,
        ...data,
        enArs: data.enArs || {
          ventasTotal: data.ventasTotal || 0,
          ventasCobradas: data.ventasCobradas || 0,
          ventasImpagas: data.ventasImpagas || 0,
          egresosTotal: data.egresosTotal || 0,
          flujoCaja: data.flujoCaja || 0,
        },
        porMoneda: data.porMoneda || {},
        monedas: data.monedas || [],
        sinCotizacion: data.sinCotizacion || 0,
      });
    } catch (err) {
      console.error('Error al cargar flujo de caja del mes:', err);
    } finally {
      setIsLoadingFlujoMes(false);
    }
  }, [token, secureFetch, isLoadingFlujoMes]);

  const fetchBudgetAlertas = useCallback(async () => {
    if (!token || !hasPresupuestosAccess) return;
    try {
      const now = new Date();
      const res = await secureFetch(`/api/budgets/status?year=${now.getFullYear()}&month=${now.getMonth() + 1}`);
      if (!res.ok) return;
      const data = await res.json();
      const alertas = (data.categorias || []).filter((c) => c.alerta);
      setBudgetAlertas(alertas);
    } catch {
      setBudgetAlertas([]);
    }
  }, [token, secureFetch, hasPresupuestosAccess]);

  const fetchBandejaInvoices = useCallback(async () => {
    if (!token) return;
    try {
      const res = await secureFetch('/api/invoices');
      if (!res.ok) return;
      const data = await res.json();
      setBandejaInvoices(Array.isArray(data) ? data : []);
    } catch {
      setBandejaInvoices([]);
    }
  }, [token, secureFetch]);

  const fetchFiscalResumen = useCallback(async () => {
    if (!token || (!canAccessPyl(user) && !hasCalendarioFiscal)) return;
    try {
      const [res, calRes] = await Promise.all([
        secureFetch('/api/fiscal/perfil'),
        secureFetch('/api/fiscal/calendario'),
      ]);
      if (!res.ok) return;
      const data = await res.json();
      let proximosCalendario = [];
      if (calRes.ok) {
        const cal = await calRes.json();
        proximosCalendario = cal.proximos || [];
      }
      setFiscalResumen({ ...(data.evaluacion || {}), proximosCalendario });
    } catch (err) {
      console.error('Error al cargar resumen fiscal:', err);
    }
  }, [token, secureFetch, user, hasCalendarioFiscal]);

  const fetchBandejaExtras = useCallback(async () => {
    if (!token || !hasProfesional) {
      setLowScoreClients([]);
      setAtRiskClients([]);
      setFiscalChecklist(null);
      return;
    }
    try {
      const [clientsRes, checklistRes, riskRes] = await Promise.all([
        secureFetch('/api/invoices-dashboard/top-clientes?periodo=año&limit=200'),
        secureFetch('/api/fiscal/checklist'),
        secureFetch('/api/cobranzas/clientes-en-riesgo'),
      ]);
      if (clientsRes.ok) {
        const list = await clientsRes.json();
        setLowScoreClients(
          (Array.isArray(list) ? list : []).filter((c) => c.collectionScore != null && c.collectionScore < 40)
        );
      }
      if (riskRes.ok) {
        const risk = await riskRes.json();
        setAtRiskClients(Array.isArray(risk.clients) ? risk.clients : []);
      } else {
        setAtRiskClients([]);
      }
      if (checklistRes.ok) {
        setFiscalChecklist(await checklistRes.json());
      }
    } catch {
      setLowScoreClients([]);
      setAtRiskClients([]);
    }
  }, [token, secureFetch, hasProfesional]);

  // Función para formatear tiempo relativo
  // Función para refrescar datos
  const handleRefresh = useCallback(async () => {
    if (refreshing || isLoadingStats) return;
    
    setRefreshing(true);
    try {
      await Promise.all([fetchStats(), fetchFlujoMes(), fetchFiscalResumen(), fetchBudgetAlertas(), fetchBandejaInvoices(), fetchBandejaExtras()]);
      setDashboardRefresh((n) => n + 1);
    } finally {
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing, isLoadingStats]);

  // Cargar datos al montar el componente
  useEffect(() => {
    if (token && !hasLoaded) {
      setHasLoaded(true);
      fetchStats();
      fetchFlujoMes();
      fetchFiscalResumen();
      fetchBudgetAlertas();
      fetchBandejaInvoices();
      fetchBandejaExtras();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasLoaded]); // Solo se ejecuta cuando cambia el token

  // Resetear flag cuando cambie el usuario
  useEffect(() => {
    setHasLoaded(false);
  }, [user]);

  useEffect(() => {
    const userId = user?.id || user?._id;
    if (!userId) return;
    try {
      const storedCollapsed = localStorage.getItem(`contsuite:onboarding-collapsed:${userId}`) === 'true';
      setOnboardingCollapsed(searchParams.get(ONBOARDING_QUERY) === '1' ? false : storedCollapsed);
    } catch {
      setOnboardingCollapsed(false);
    }
  }, [user, searchParams]);

  useEffect(() => {
    if (searchParams.get(ONBOARDING_QUERY) !== '1' || loading) return;
    const timer = window.setTimeout(() => {
      document.getElementById('onboarding-checklist')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [searchParams, loading]);

  const scrollToMonthClose = useCallback(() => {
    document.getElementById('month-close-checklist')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (loading || window.location.hash !== '#month-close-checklist') return;
    const timer = window.setTimeout(scrollToMonthClose, 200);
    return () => window.clearTimeout(timer);
  }, [loading, scrollToMonthClose]);

  const flujoMes = useMemo(() => pickFlujo(flujoRaw, currencyVista), [flujoRaw, currencyVista]);
  const cobrarDia = useMemo(
    () => buildCobrarHoy({ invoices: bandejaInvoices, profesional: hasProfesional }),
    [bandejaInvoices, hasProfesional]
  );
  const bandejaItems = useMemo(
    () => buildBandeja({
      invoices: bandejaInvoices,
      fiscal: fiscalResumen,
      sinCotizacion: flujoRaw.sinCotizacion || 0,
      lowScoreClients,
      atRiskClients,
      fiscalChecklist,
      hasCobrarHoy: cobrarDia.tasks.length > 0,
    }),
    [bandejaInvoices, fiscalResumen, flujoRaw.sinCotizacion, lowScoreClients, atRiskClients, fiscalChecklist, cobrarDia.tasks.length]
  );
  const flujoCurrency = displayCurrency(currencyVista);
  const availableMonedas = useMemo(
    () => mergeMonedas(flujoRaw.monedas, dashboardMonedas),
    [flujoRaw.monedas, dashboardMonedas]
  );
  const showCurrencyToggle = hasMultimoneda(user) && shouldShowCurrencyToggle(availableMonedas);
  const mesLabel = new Date().toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

  const onboardingProgress = getOnboardingProgress(stats);
  const activationComplete = isActivationComplete(onboardingProgress);
  const showOnboarding = !loading && !isOnboardingComplete(onboardingProgress);
  const showDashboardExtras = !showOnboarding || activationComplete;
  const toggleOnboarding = () => {
    const next = !onboardingCollapsed;
    setOnboardingCollapsed(next);
    const userId = user?.id || user?._id;
    if (!userId) return;
    try {
      localStorage.setItem(`contsuite:onboarding-collapsed:${userId}`, String(next));
    } catch {
      // La preferencia solo se conserva durante esta sesión si el almacenamiento no está disponible.
    }
  };

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className={modoOscuro ? 'w-full bg-black text-white' : 'w-full bg-[#f5f5f7] text-gray-900'}>
      {/* Loading Overlay */}
      {loading && (
        <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]'}`}>
          {/* Logo pulse animation */}
          <div className="relative">
            <div className="animate-pulse">
              <img
                src={modoOscuro ? "/logowhite.png" : "/logoblack.png"}
                alt="Contsuite"
                className="h-12 w-auto"
              />
            </div>
            {/* Ripple rings */}
            <div className="absolute inset-0 -m-8">
              <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-500/30' : 'border border-blue-500/30'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]`} />
              <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-400/20' : 'border border-blue-400/20'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_0.5s]`} />
            </div>
          </div>
          {/* Loading text */}
          <div className="mt-8 text-center space-y-2">
            <p className={`text-sm font-medium ${textPri}`}>
              Cargando tu dashboard
            </p>
            <p className={`text-xs ${textSec}`}>
              Preparando tus datos financieros...
            </p>
          </div>
        </div>
      )}

      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-4 sm:py-5 lg:py-6 pb-12 sm:pb-16 space-y-4 sm:space-y-5">
          <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight ${textPri}`}>
                Hola, <span className="text-[#1f80ff]">{user?.name?.split(' ')[0] || 'Usuario'}</span>
              </h1>
              <div className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-1.5 text-sm ${textSec}`}>
                <button
                  type="button"
                  onClick={() => navigate('/planes')}
                  title={planInfo.tagline}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border transition-colors hover:opacity-90 ${getPlanBadgeClasses(planInfo.slug, modoOscuro)}`}
                >
                  <FiAward className="w-3 h-3 shrink-0" />
                  Plan {planInfo.name}
                </button>
                <BetaProPageBanner user={user} />
                <span className={textMuted}>·</span>
                <span>Tu resumen de {mesLabel}</span>
                {lastUpdated && (
                  <>
                    <span className={textMuted}>·</span>
                    <span className={`text-xs ${textMuted}`}>
                      Actualizado {lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasPylAccess && (
                <button
                  type="button"
                  onClick={() => navigate(fiscalResumen?.regimen ? '/reportes?tab=pyl' : '/cuenta?tab=fiscal')}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800 text-gray-200' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <FiPieChart className="w-3.5 h-3.5 text-[#1F80FF]" />
                  Fiscal
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/auditoria')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800 text-gray-200' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                }`}
              >
                <FiActivity className="w-3.5 h-3.5 text-[#1F80FF]" />
                Auditoría
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50 ${
                  modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <FiRefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>
          </header>

          <FormAlert
            type="error"
            action={
              <button
                type="button"
                onClick={handleRefresh}
                className="text-xs font-semibold underline underline-offset-2 shrink-0"
              >
                Reintentar
              </button>
            }
          >
            {error}
          </FormAlert>

          {isLedgerAccount && (
            <PlatformLedgerPanel modoOscuro={modoOscuro} variant="inicio" />
          )}

          {showDashboardExtras && !readOnly && (
            <CollectionCycleMonthCard
              secureFetch={secureFetch}
              hasProfesional={hasProfesional}
              modoOscuro={modoOscuro}
              box={box}
              textPri={textPri}
              textSec={textSec}
              textMuted={textMuted}
            />
          )}

          {/* Panel principal: flujo de caja del mes + métricas (incluye atajo a uso del plan) */}
          <section className={`rounded-2xl border overflow-hidden ${box}`}>
            <div className={`p-4 sm:p-5 ${modoOscuro ? 'border-b border-gray-700/80' : 'border-b border-gray-200'}`}>
              {showCurrencyToggle && (
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <CurrencyViewToggle
                    value={currencyVista}
                    onChange={setCurrencyVista}
                    monedas={availableMonedas}
                    modoOscuro={modoOscuro}
                  />
                  <p className={`text-[11px] ${textMuted}`}>{vistaHint(currencyVista)}</p>
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate('/reportes')}
                className={`w-full text-left rounded-xl transition-colors ${modoOscuro ? 'hover:bg-gray-800/60' : 'hover:bg-gray-50'}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-6">
                  <div className="shrink-0">
                    <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>Flujo de caja del mes</div>
                    <div className={`text-2xl sm:text-3xl font-semibold mt-0.5 ${flujoMes.flujoCaja >= 0 ? (modoOscuro ? 'text-green-300' : 'text-green-700') : (modoOscuro ? 'text-red-300' : 'text-red-700')}`}>
                      {isLoadingFlujoMes ? 'Cargando…' : formatMoney(flujoMes.flujoCaja, flujoCurrency)}
                    </div>
                    <div className={`text-[11px] mt-0.5 ${textMuted}`}>
                      Cobros − Gastos
                      {currencyVista === VISTA_CONSOLIDADO && showCurrencyToggle ? ' · en pesos' : ''}
                    </div>
                    {flujoRaw.sinCotizacion > 0 && currencyVista === VISTA_CONSOLIDADO && (
                      <div className={`text-[11px] mt-1 ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
                        {flujoRaw.sinCotizacion} registro{flujoRaw.sinCotizacion !== 1 ? 's' : ''} sin cotización no {flujoRaw.sinCotizacion !== 1 ? 'están' : 'está'} en el consolidado
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full lg:flex-1 lg:max-w-2xl">
                    <div className={`px-2.5 py-2 rounded-lg text-center ${modoOscuro ? 'bg-gray-800/80' : 'bg-gray-50'}`}>
                      <div className={`text-[10px] uppercase ${textMuted}`}>Ventas</div>
                      <div className={`text-sm font-semibold ${textPri}`}>{isLoadingFlujoMes ? '—' : formatMoney(flujoMes.ventasTotal, flujoCurrency)}</div>
                    </div>
                    <div className={`px-2.5 py-2 rounded-lg text-center ${modoOscuro ? 'bg-gray-800/80' : 'bg-gray-50'}`}>
                      <div className={`text-[10px] uppercase ${textMuted}`}>Cobros</div>
                      <div className={`text-sm font-semibold ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}>{isLoadingFlujoMes ? '—' : formatMoney(flujoMes.ventasCobradas, flujoCurrency)}</div>
                    </div>
                    <div className={`px-2.5 py-2 rounded-lg text-center ${modoOscuro ? 'bg-gray-800/80' : 'bg-gray-50'}`}>
                      <div className={`text-[10px] uppercase ${textMuted}`}>Por cobrar</div>
                      <div className={`text-sm font-semibold ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>{isLoadingFlujoMes ? '—' : formatMoney(flujoMes.ventasImpagas, flujoCurrency)}</div>
                    </div>
                    <div className={`px-2.5 py-2 rounded-lg text-center ${modoOscuro ? 'bg-gray-800/80' : 'bg-gray-50'}`}>
                      <div className={`text-[10px] uppercase ${textMuted}`}>Gastos</div>
                      <div className={`text-sm font-semibold ${modoOscuro ? 'text-red-300' : 'text-red-700'}`}>{isLoadingFlujoMes ? '—' : formatMoney(flujoMes.egresosTotal, flujoCurrency)}</div>
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <HomeDashboardSection
              refreshTrigger={dashboardRefresh}
              embedded
              inPanel
              vista={currencyVista}
              currency={flujoCurrency}
              onMonedas={setDashboardMonedas}
              onVistaChange={setCurrencyVista}
              currencyMonedas={availableMonedas}
              showCurrencyToggle={showCurrencyToggle}
            />
          </section>

          {showOnboarding && (
            <OnboardingChecklist
              stats={stats}
              modoOscuro={modoOscuro}
              collapsed={onboardingCollapsed}
              onToggleCollapsed={toggleOnboarding}
              onNavigate={navigate}
            />
          )}

          {showOnboarding && !readOnly && (
            <ContextualAssistantStrip
              contextId="onboarding"
              secureFetch={secureFetch}
              hasProfesional={hasProfesional}
              modoOscuro={modoOscuro}
              textSec={textSec}
              textMuted={textMuted}
              defaultExpanded
            />
          )}

          {!readOnly && (
            <ContadorNoteBanner
              secureFetch={secureFetch}
              modoOscuro={modoOscuro}
              box={box}
              textPri={textPri}
              textSec={textSec}
              textMuted={textMuted}
            />
          )}

          {!readOnly && (
            <WeeklyGuidanceCard
              secureFetch={secureFetch}
              hasProfesional={hasProfesional}
              modoOscuro={modoOscuro}
              box={box}
              textPri={textPri}
              textSec={textSec}
              textMuted={textMuted}
            />
          )}

          {showDashboardExtras && hasProfesional && !readOnly && (
            <MonthCompareCard
              modoOscuro={modoOscuro}
              vista={currencyVista}
              currency={flujoCurrency}
              refreshTrigger={dashboardRefresh}
            />
          )}

          {showDashboardExtras && activationComplete && hasProfesional && !readOnly && (
            <ContadorPuenteCard
              hasProfesional={hasProfesional}
              modoOscuro={modoOscuro}
              box={box}
              textPri={textPri}
              textSec={textSec}
              textMuted={textMuted}
              onNavigate={navigate}
              onGoToCierre={scrollToMonthClose}
            />
          )}

          {showDashboardExtras && hasProfesional && !readOnly && (
            <MonthCloseChecklist modoOscuro={modoOscuro} onNavigate={navigate} />
          )}

          {showDashboardExtras && !readOnly && (
            <BandejaMes
              cobrarDia={cobrarDia}
              alertItems={bandejaItems}
              modoOscuro={modoOscuro}
              hasProfesional={hasProfesional}
              variant="compact"
              onOpen={(href) => navigate(href)}
            />
          )}

          <CobranzasInicioWidget
            secureFetch={secureFetch}
            user={user}
            hasProfesional={hasProfesional}
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textSec={textSec}
            textMuted={textMuted}
          />

          {hasPylAccess && fiscalResumen?.regimen === 'monotributo' && fiscalResumen.topeCategoria != null && (
            <div className={`rounded-2xl border p-3 sm:p-4 ${box}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>
                  Monotributo{fiscalResumen.categoria ? ` ${fiscalResumen.categoria}` : ''} · tope anual
                </div>
                <span className={`text-xs font-semibold tabular-nums ${textPri}`}>
                  {(fiscalResumen.porcentajeTope || 0) > 0 && (fiscalResumen.porcentajeTope || 0) < 1
                    ? '<1%'
                    : `${Math.round(fiscalResumen.porcentajeTope || 0)}%`}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                <div>
                  <div className={`text-[10px] uppercase ${textMuted}`}>Facturado 12 m</div>
                  <div className={`text-sm font-semibold ${textPri}`}>{formatMoney(fiscalResumen.facturacionAcumulada12m)}</div>
                  {Object.keys(fiscalResumen.porMoneda || {}).some((m) => m !== 'ARS') && (
                    <div className={`text-[10px] ${textMuted}`}>incluye divisas en pesos</div>
                  )}
                </div>
                <div>
                  <div className={`text-[10px] uppercase ${textMuted}`}>Tope</div>
                  <div className={`text-sm font-semibold ${textPri}`}>{formatMoney(fiscalResumen.topeCategoria)}</div>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <div className={`text-[10px] uppercase ${textMuted}`}>Uso del tope</div>
                  <div className={`text-sm font-semibold ${textPri}`}>
                    {(fiscalResumen.porcentajeTope || 0) > 0 && (fiscalResumen.porcentajeTope || 0) < 1
                      ? '<1%'
                      : `${Math.round(fiscalResumen.porcentajeTope || 0)}%`}
                  </div>
                </div>
              </div>
              <div className={`h-1.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
                <div
                  className={`h-full ${(fiscalResumen.porcentajeTope || 0) >= 100 ? 'bg-red-500' : (fiscalResumen.porcentajeTope || 0) >= 80 ? 'bg-amber-500' : 'bg-[#1F80FF]'}`}
                  style={{ width: `${Math.min(Math.max(fiscalResumen.porcentajeTope || 0, 0), 100)}%` }}
                />
              </div>
              {runRateCopy(fiscalResumen) && (
                <p className={`text-xs mt-2 ${textMuted}`}>{runRateCopy(fiscalResumen)}</p>
              )}
              {hasProfesional && (
                <button
                  type="button"
                  onClick={() => navigate('/cuenta?tab=fiscal#simulador')}
                  className="mt-3 text-xs font-semibold text-[#1F80FF] hover:underline"
                >
                  Simular escenarios de categoría →
                </button>
              )}
            </div>
          )}

          {hasPresupuestosAccess && budgetAlertas.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/reportes?tab=presupuestos')}
              className={`w-full rounded-2xl border p-4 text-left transition-colors ${
                modoOscuro
                  ? 'border-amber-800/60 bg-amber-950/30 hover:bg-amber-950/50'
                  : 'border-amber-200 bg-amber-50 hover:bg-amber-100/80'
              }`}
            >
              <div className="flex items-start gap-3">
                <FiAlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`} />
                <div>
                  <p className={`text-sm font-semibold ${textPri}`}>
                    {budgetAlertas.length} categoría{budgetAlertas.length !== 1 ? 's' : ''} con alerta de presupuesto
                  </p>
                  <p className={`text-xs mt-1 ${textSec}`}>
                    {budgetAlertas.slice(0, 3).map((a) => {
                      const pct = a.porcentaje != null ? Math.round(a.porcentaje * 100) : null;
                      const tag = a.alerta === 'excedido' ? 'excedido' : `${pct ?? 80}%`;
                      return `${a.categoria} ${tag}`;
                    }).join(' · ')}
                    {budgetAlertas.length > 3 ? '…' : ''}
                    {' — '}comparado en pesos. Ver detalle en Presupuestos
                  </p>
                </div>
              </div>
            </button>
          )}

          {!permissionsLoading && !hasDashboardMetrics && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
              <div className={`${box} rounded-xl p-3 text-center`}>
                <div className={`text-xl font-semibold ${textPri}`}>{loading ? '—' : stats.clientes}</div>
                <div className={`text-[11px] uppercase ${textMuted}`}>Clientes</div>
              </div>
              <div className={`${box} rounded-xl p-3 text-center`}>
                <div className={`text-xl font-semibold ${textPri}`}>{loading ? '—' : stats.facturas}</div>
                <div className={`text-[11px] uppercase ${textMuted}`}>Ventas totales</div>
              </div>
              <button
                type="button"
                onClick={() => stats.pendientes > 0 && navigate('/facturas?estado=impaga')}
                className={`${box} rounded-xl p-3 text-center ${stats.pendientes > 0 ? 'hover:shadow-md cursor-pointer' : 'cursor-default'}`}
              >
                <div className={`text-xl font-semibold ${textPri}`}>{loading ? '—' : stats.pendientes}</div>
                <div className={`text-[11px] uppercase ${textMuted}`}>Impagas</div>
              </button>
              <div className={`${box} rounded-xl p-3 text-center`}>
                <div className={`text-xl font-semibold ${textPri}`}>{loading ? '—' : `${stats.eficiencia}%`}</div>
                <div className={`text-[11px] uppercase ${textMuted}`}>Cobro del mes</div>
              </div>
            </div>
          )}

      </div>
    </div>
  );
};

export default Inicio;
